import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { getDownloadURL, getStorage, ref, uploadString } from "firebase/storage";
import { fetchCategories, upsertPost } from "../../redux/blogSlice";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bold, Italic, Underline, Link, Image, Camera,
  Type, AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, CheckCircle, AlertCircle,
  Loader2, ChevronDown, X, Minus,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────
   FONTS
───────────────────────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   THEME  —  pure white · ink black · zero colour
───────────────────────────────────────────────────────── */
const C = {
  paper:        "#ffffff",
  surface:      "#f7f7f7",
  surface2:     "#f0f0f0",
  ink:          "#0a0a0a",
  inkSoft:      "#2a2a2a",
  inkMid:       "#6b6b6b",
  inkLight:     "#a3a3a3",
  border:       "#e0e0e0",
  border2:      "#cacaca",
  success:      "#1a7a3a",
  successBg:    "#f0faf4",
  successBorder:"#b8dfc8",
  danger:       "#b22222",
  dangerBg:     "#fff5f5",
  dangerBorder: "#f0c8c8",
  serif:        "'Cormorant Garamond', Georgia, serif",
  sans:         "'Montserrat', sans-serif",
};

/* ─────────────────────────────────────────────────────────
   SMALL REUSABLE COMPONENTS
───────────────────────────────────────────────────────── */
const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <label style={S.label}>
    {children}
    {required && <span style={{ color: C.danger, marginLeft: 3 }}>*</span>}
  </label>
);

const FieldInput: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { monospace?: boolean }> = ({ monospace, style, ...props }) => (
  <input
    {...props}
    style={{
      ...S.input,
      fontFamily: monospace ? "'Courier New', monospace" : C.sans,
      ...style,
    }}
    className="ep-input"
  />
);

const FieldTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ style, ...props }) => (
  <textarea {...props} style={{ ...S.textarea, ...style }} className="ep-input"/>
);

/* Toolbar icon button */
const TBtn: React.FC<{
  title: string;
  onClick: (e: React.MouseEvent) => void;
  active?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ title, onClick, active, danger, children }) => (
  <button
    title={title}
    onMouseDown={e => { e.preventDefault(); onClick(e); }}
    style={{
      ...S.tbtn,
      background: active ? C.ink : "transparent",
      color:  active ? "#fff" : danger ? C.danger : C.inkMid,
      border: `1px solid ${active ? C.ink : C.border}`,
    }}
    className="ep-tbtn"
  >
    {children}
  </button>
);

const Divider = () => (
  <div style={{ width:1, height:22, background:C.border, margin:"0 2px", flexShrink:0 }}/>
);

/* ═══════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════ */
const EPost: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const categories = useSelector((state: RootState) => state.blog.categories);

  const editorRef = useRef<HTMLDivElement>(null);

  /* ── form state ── */
  const [title,         setTitle]         = useState("");
  const [slug,          setSlug]          = useState("");
  const [category,      setCategory]      = useState("");
  const [description,   setDescription]   = useState("");
  const [featuredImage, setFeaturedImage] = useState<string | null>(null);
  const [textColor,     setTextColor]     = useState("#0a0a0a");

  /* ── UI state ── */
  const [loading,      setLoading]      = useState(false);
  const [success,      setSuccess]      = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [slugEdited,   setSlugEdited]   = useState(false);
  const [linkPopup,    setLinkPopup]    = useState(false);
  const [linkUrl,      setLinkUrl]      = useState("");
  const [colorPicker,  setColorPicker]  = useState(false);
  const [charCount,    setCharCount]    = useState(0);
  const [wordCount,    setWordCount]    = useState(0);
  const [focused,      setFocused]      = useState(false);
  const linkInputRef   = useRef<HTMLInputElement>(null);
  const colorInputRef  = useRef<HTMLInputElement>(null);

  useEffect(() => { dispatch(fetchCategories()); }, [dispatch]);

  /* ── Slug auto-gen ── */
  const generateSlug = (text: string) =>
    text.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const handleTitleChange = (val: string) => {
    setTitle(val);
    if (!slugEdited) setSlug(generateSlug(val));
  };

  /* ── execCommand wrapper ── */
  const exec = (command: string, value = "") => {
    if (command === "foreColor" && window.getSelection()?.isCollapsed) {
      const span = document.createElement("span");
      span.style.color = value;
      span.appendChild(document.createTextNode("\u200b"));
      const range = window.getSelection()?.getRangeAt(0);
      if (range) {
        range.insertNode(span);
        range.setStart(span.firstChild!, 1);
        range.setEnd(span.firstChild!, 1);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
      }
    } else {
      document.execCommand(command, false, value);
    }
    editorRef.current?.focus();
  };

  /* ── Insert inline image via FileReader ── */
  const insertImage = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = document.createElement("img");
      img.src   = reader.result as string;
      img.style.cssText = "max-width:100%;border-radius:4px;margin:12px 0;display:block;";
      const range = window.getSelection()?.getRangeAt(0);
      if (range) {
        range.insertNode(img);
        range.setStartAfter(img);
        range.collapse(true);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
      }
      editorRef.current?.focus();
    };
    reader.readAsDataURL(file);
  };

  /* ── Link insert ── */
  const handleLinkInsert = () => {
    if (!linkUrl.trim()) return;
    exec("createLink", linkUrl);
    setLinkUrl("");
    setLinkPopup(false);
    editorRef.current?.focus();
  };

  /* ── Upload base64 images to Firebase ── */
  const uploadBase64Image = async (base64: string, index: number): Promise<string> => {
    const storage  = getStorage();
    const imageRef = ref(storage, `posts/editorImage-${Date.now()}-${index}.jpg`);
    await uploadString(imageRef, base64, "data_url");
    return await getDownloadURL(imageRef);
  };

  /* ── Live word / char count ── */
  const updateCounts = () => {
    const text = editorRef.current?.innerText || "";
    setCharCount(text.length);
    setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0);
  };

  /* ── Publish ── */
  const handlePublish = async () => {
    let content = editorRef.current?.innerHTML || "";
    if (!title.trim()) { setError("Please add a post title."); return; }
    if (!content.trim() || content === "<br>") { setError("Please write some content."); return; }

    setLoading(true);
    setError(null);
    const postSlug = slug || generateSlug(title);

    try {
      /* Replace base64 images with uploaded URLs */
      const base64Matches = content.match(/<img[^>]+src="data:image\/[^"]+"[^>]*>/g) || [];
      for (let i = 0; i < base64Matches.length; i++) {
        const tag         = base64Matches[i];
        const base64Match = tag.match(/src="(data:image\/[^"]+)"/);
        if (!base64Match) continue;
        const url    = await uploadBase64Image(base64Match[1], i);
        const newTag = tag.replace(base64Match[1], url);
        content      = content.replace(tag, newTag);
      }

      await dispatch(upsertPost({
        title,
        slug:          postSlug,
        content,
        featuredImage: featuredImage || "",
        category:      category || undefined,
        description,
        published:     true,
      })).unwrap();

      /* Reset */
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3500);
      setTitle(""); setSlug(""); setSlugEdited(false);
      setDescription(""); setCategory(""); setFeaturedImage(null);
      if (editorRef.current) editorRef.current.innerHTML = "";
      setCharCount(0); setWordCount(0);
    } catch (err: any) {
      console.error("Failed to publish:", err);
      setError(err?.message || "Failed to publish. Please try again.");
    }

    setLoading(false);
  };

  /* ─── RENDER ─── */
  return (
    <div style={{ background:C.paper, fontFamily:C.sans, color:C.ink }}>
      <style>{FONT_CSS + EPOST_CSS}</style>

      {/* ── TITLE STRIP (embedded in Editorial shell — no fixed/sticky header) ── */}
      <div style={S.titleStrip}>
        <div style={{ height:2, background:C.ink, marginBottom:16 }}/>
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:16 }}>
          <div>
            <p style={S.headerEye}>CONTENT MANAGEMENT</p>
            <h1 style={S.headerTitle}>New Post</h1>
          </div>
          <div style={{ display:"flex", gap:8, paddingBottom:4 }}>
            <span style={S.metaPill}>{wordCount} words</span>
            <span style={S.metaPill}>{charCount} chars</span>
          </div>
        </div>
        <div style={{ height:1, background:C.border }}/>
      </div>

      <div style={S.body} className="ep-body-wrap">

        {/* ══════════════════════════════
            TOAST
        ══════════════════════════════ */}
        <AnimatePresence>
          {(success || error) && (
            <motion.div
              key="toast"
              initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }}
              transition={{ duration:0.22 }}
              style={{
                display:"flex", alignItems:"center", gap:10,
                padding:"13px 16px", marginBottom:24,
                background: success ? C.successBg : C.dangerBg,
                border: `1px solid ${success ? C.successBorder : C.dangerBorder}`,
              }}
            >
              {success
                ? <CheckCircle size={15} color={C.success}/>
                : <AlertCircle size={15} color={C.danger}/>
              }
              <span style={{ fontSize:12, fontWeight:500, color: success ? C.success : C.danger, flex:1 }}>
                {success ? "Post published successfully!" : error}
              </span>
              {error && (
                <button onClick={() => setError(null)} style={{ background:"none", border:"none", cursor:"pointer", color:C.inkMid }}>
                  <X size={13}/>
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <div style={S.layout} className="ep-layout">

          {/* ══════════════════════════════
              LEFT — EDITOR COLUMN
          ══════════════════════════════ */}
          <div style={S.editorCol}>

            {/* Title */}
            <div style={S.fieldGroup}>
              <Label required>Post Title</Label>
              <input
                type="text"
                placeholder="Write a compelling headline…"
                value={title}
                onChange={e => handleTitleChange(e.target.value)}
                style={S.titleInput}
                className="ep-input ep-title"
              />
            </div>

            {/* Slug */}
            <div style={S.fieldGroup}>
              <Label>URL Slug</Label>
              <div style={S.slugWrap}>
                <span style={S.slugPrefix}>4reva.com/</span>
                <input
                  type="text"
                  placeholder="auto-generated-from-title"
                  value={slug}
                  onChange={e => { setSlug(generateSlug(e.target.value)); setSlugEdited(true); }}
                  style={S.slugInput}
                  className="ep-input"
                />
                {slugEdited && (
                  <button
                    onClick={() => { setSlug(generateSlug(title)); setSlugEdited(false); }}
                    style={S.slugReset}
                    title="Reset to auto-generated"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* Description */}
            <div style={S.fieldGroup}>
              <Label>Description / Excerpt</Label>
              <FieldTextarea
                placeholder="A short summary shown in listings and SEO previews…"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            {/* ── RICH EDITOR ── */}
            <div style={S.fieldGroup}>
              <Label required>Content</Label>

              {/* Toolbar */}
              <div style={S.toolbar} className="ep-toolbar">

                {/* Text formatting */}
                <TBtn title="Bold (Ctrl+B)"      onClick={() => exec("bold")}>      <Bold size={13}/></TBtn>
                <TBtn title="Italic (Ctrl+I)"    onClick={() => exec("italic")}>    <Italic size={13}/></TBtn>
                <TBtn title="Underline (Ctrl+U)" onClick={() => exec("underline")}> <Underline size={13}/></TBtn>

                <Divider/>

                {/* Alignment */}
                <TBtn title="Align left"   onClick={() => exec("justifyLeft")}>   <AlignLeft size={13}/></TBtn>
                <TBtn title="Align center" onClick={() => exec("justifyCenter")}> <AlignCenter size={13}/></TBtn>
                <TBtn title="Align right"  onClick={() => exec("justifyRight")}>  <AlignRight size={13}/></TBtn>

                <Divider/>

                {/* Lists */}
                <TBtn title="Bullet list"   onClick={() => exec("insertUnorderedList")}> <List size={13}/></TBtn>
                <TBtn title="Numbered list" onClick={() => exec("insertOrderedList")}>   <ListOrdered size={13}/></TBtn>

                <Divider/>

                {/* Heading */}
                <TBtn title="Heading H2" onClick={() => exec("formatBlock", "h2")}>
                  <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.05em" }}>H2</span>
                </TBtn>
                <TBtn title="Heading H3" onClick={() => exec("formatBlock", "h3")}>
                  <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.05em" }}>H3</span>
                </TBtn>
                <TBtn title="Paragraph" onClick={() => exec("formatBlock", "p")}>
                  <AlignLeft size={12}/>
                </TBtn>

                <Divider/>

                {/* Link */}
                <div style={{ position:"relative" }}>
                  <TBtn title="Insert link" onClick={() => { setLinkPopup(v => !v); setTimeout(() => linkInputRef.current?.focus(), 50); }} active={linkPopup}>
                    <Link size={13}/>
                  </TBtn>
                  <AnimatePresence>
                    {linkPopup && (
                      <motion.div
                        initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-4 }}
                        transition={{ duration:0.15 }}
                        style={S.linkPopup}
                      >
                        <input
                          ref={linkInputRef}
                          type="url"
                          placeholder="https://example.com"
                          value={linkUrl}
                          onChange={e => setLinkUrl(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && handleLinkInsert()}
                          style={S.linkInput}
                          className="ep-input"
                        />
                        <button onClick={handleLinkInsert} style={S.linkInsertBtn}>Insert</button>
                        <button onClick={() => setLinkPopup(false)} style={S.linkCancelBtn}><X size={12}/></button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Inline image */}
                <label title="Insert image" style={S.tbtnLabel}>
                  <Image size={13}/>
                  <input type="file" accept="image/*" multiple hidden
                    onChange={e => {
                      const files = e.target.files;
                      if (files) Array.from(files).forEach(f => insertImage(f));
                      e.target.value = "";
                    }}
                  />
                </label>

                {/* Text colour */}
                <div style={{ position:"relative" }}>
                  <TBtn title="Text colour" onClick={() => { setColorPicker(v => !v); setTimeout(() => colorInputRef.current?.click(), 50); }}>
                    <span style={{ fontSize:11, fontWeight:700, color:textColor, borderBottom:`2px solid ${textColor}`, lineHeight:1 }}>A</span>
                  </TBtn>
                  {colorPicker && (
                    <div style={{ position:"absolute", top:"110%", left:0, zIndex:200, padding:4, background:C.paper, border:`1px solid ${C.border}`, boxShadow:"0 4px 16px rgba(0,0,0,0.08)" }}>
                      <input
                        ref={colorInputRef}
                        type="color"
                        value={textColor}
                        onChange={e => {
                          const c = e.target.value;
                          setTextColor(c);
                          exec("foreColor", c);
                        }}
                        onBlur={() => setColorPicker(false)}
                        style={{ width:36, height:36, border:"none", cursor:"pointer", background:"transparent", display:"block" }}
                      />
                    </div>
                  )}
                </div>

                <Divider/>

                {/* Clear formatting */}
                <TBtn title="Remove formatting" onClick={() => exec("removeFormat")}>
                  <Minus size={13}/>
                </TBtn>

              </div>

              {/* Content-editable area */}
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={updateCounts}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                style={{
                  ...S.editor,
                  border: `1px solid ${focused ? C.ink : C.border}`,
                }}
className="ep-editor ep-editor-area"
                data-placeholder="Start writing your post here…"
              />

              {/* Word count bar */}
              <div style={S.countBar}>
                <span>{wordCount} words · {charCount} characters</span>
                <span>{Math.max(1, Math.round(wordCount / 220))} min read</span>
              </div>
            </div>

            {/* Publish button */}
            <motion.button
              whileTap={{ scale: 0.97 } as any}
              onClick={handlePublish}
              disabled={loading}
              style={{
                ...S.publishBtn,
                opacity: loading ? 0.65 : 1,
                cursor:  loading ? "not-allowed" : "pointer",
              }}
              className="ep-publish-btn"
            >
              {loading
                ? <><Loader2 size={15} className="ep-spin" style={{ marginRight:8 }}/> PUBLISHING…</>
                : "PUBLISH POST"
              }
            </motion.button>

          </div>

          {/* ══════════════════════════════
              RIGHT — META SIDEBAR
          ══════════════════════════════ */}
          <div style={S.sidebar}>

            {/* Featured image */}
<div style={S.sideCard} className="ep-side-card">
              <p style={S.sideCardTitle}>FEATURED IMAGE</p>

              {featuredImage ? (
                <div style={{ position:"relative" }}>
                  <img src={featuredImage} alt="Featured" style={S.featuredImg}/>
                  <button
                    onClick={() => setFeaturedImage(null)}
                    style={S.featuredRemove}
                    title="Remove image"
                  >
                    <X size={12}/>
                  </button>
                </div>
              ) : (
                <label style={S.featuredUpload}>
                  <Camera size={22} color={C.inkLight} strokeWidth={1.5}/>
                  <span style={{ fontSize:11, color:C.inkLight, marginTop:6 }}>Upload cover image</span>
                  <span style={{ fontSize:9, color:C.inkLight, marginTop:2 }}>JPG, PNG, WEBP</span>
                  <input
                    type="file" accept="image/*" hidden
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => setFeaturedImage(reader.result as string);
                      reader.readAsDataURL(file);
                    }}
                  />
                </label>
              )}
            </div>

            {/* Category */}
<div style={S.sideCard} className="ep-side-card">
              <p style={S.sideCardTitle}>CATEGORY</p>
              <div style={{ position:"relative" }}>
                <select
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  style={S.select}
                  className="ep-input"
                >
                  <option value="">— None —</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.slug}>{cat.title}</option>
                  ))}
                </select>
                <ChevronDown size={13} color={C.inkMid} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}/>
              </div>
            </div>

            {/* SEO preview */}
<div style={S.sideCard} className="ep-side-card">
              <p style={S.sideCardTitle}>SEO PREVIEW</p>
              <div style={S.seoPreview}>
                <p style={S.seoTitle}>{title || "Post title"}</p>
                <p style={S.seoUrl}>4reva.com/{slug || "your-slug"}</p>
                <p style={S.seoDesc}>{description || "Your description will appear here in search results."}</p>
              </div>
            </div>

            {/* Checklist */}
<div style={S.sideCard} className="ep-side-card">
              <p style={S.sideCardTitle}>PUBLISH CHECKLIST</p>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {[
                  { label:"Title added",          done: title.trim().length > 0 },
                  { label:"Slug configured",       done: slug.trim().length > 0 },
                  { label:"Description written",   done: description.trim().length > 0 },
                  { label:"Content added",         done: wordCount > 0 },
                  { label:"Featured image set",    done: !!featuredImage },
                  { label:"Category selected",     done: !!category },
                ].map(item => (
                  <div key={item.label} style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{
                      width:16, height:16, borderRadius:"50%", flexShrink:0,
                      background: item.done ? C.ink : "transparent",
                      border: `1.5px solid ${item.done ? C.ink : C.border}`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                    }}>
                      {item.done && <div style={{ width:5, height:5, borderRadius:"50%", background:"#fff" }}/>}
                    </div>
                    <span style={{ fontSize:11, color: item.done ? C.ink : C.inkLight, fontFamily:C.sans }}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default EPost;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {

  /* Title strip — slim, no sticky, embeds in shell */
  titleStrip:  { padding:"20px 20px 0" },
  headerEye: {
    fontSize:9, letterSpacing:"0.3em", color:C.inkMid,
    fontFamily:C.sans, marginBottom:6,
  },
  headerTitle: {
    fontFamily:C.serif, fontSize:"clamp(26px,4vw,42px)" as any, fontWeight:600,
    color:C.ink, lineHeight:1, margin:0, letterSpacing:"-0.01em",
  },
  metaPill: {
    fontSize:9, letterSpacing:"0.16em", color:C.inkMid,
    border:`1px solid ${C.border}`, padding:"3px 10px",
    fontFamily:C.sans,
  },

  body:   { maxWidth:1160, margin:"0 auto", padding:"28px 20px 60px" },
  /* layout — CSS class ep-layout handles the breakpoint */
  layout: { display:"grid", gridTemplateColumns:"1fr 300px", gap:32, alignItems:"start" },
  editorCol: { display:"flex", flexDirection:"column", gap:22 },
  fieldGroup:{ display:"flex", flexDirection:"column", gap:7 },

  label: {
    fontSize:9, letterSpacing:"0.22em", color:C.inkMid,
    fontFamily:C.sans, fontWeight:600,
  },
  input: {
    width:"100%", background:C.paper, border:`1px solid ${C.border}`,
    padding:"11px 14px", fontSize:13, color:C.ink,
    fontFamily:C.sans, outline:"none", boxSizing:"border-box" as const,
    transition:"border-color 0.2s",
  },
  titleInput: {
    width:"100%", background:C.paper, border:`1px solid ${C.border}`,
    padding:"14px 16px", fontSize:20, fontWeight:600, color:C.ink,
    fontFamily:C.serif, outline:"none", boxSizing:"border-box" as const,
    letterSpacing:"-0.01em", transition:"border-color 0.2s",
  },
  textarea: {
    width:"100%", background:C.paper, border:`1px solid ${C.border}`,
    padding:"11px 14px", fontSize:13, color:C.ink,
    fontFamily:C.sans, fontWeight:300, outline:"none",
    resize:"vertical", boxSizing:"border-box" as const,
    transition:"border-color 0.2s", lineHeight:1.75,
  },

  /* Slug */
  slugWrap:   { display:"flex", alignItems:"center", border:`1px solid ${C.border}`, background:C.paper, transition:"border-color 0.2s" },
  slugPrefix: { fontSize:12, color:C.inkLight, padding:"0 12px", flexShrink:0, borderRight:`1px solid ${C.border}`, lineHeight:"42px" },
  slugInput:  { flex:1, background:"transparent", border:"none", outline:"none", padding:"11px 12px", fontSize:12, color:C.ink, fontFamily:"'Courier New',monospace", letterSpacing:"0.02em" },
  slugReset:  { fontSize:9, color:C.inkMid, letterSpacing:"0.1em", background:"none", border:"none", cursor:"pointer", padding:"0 12px", fontFamily:C.sans, flexShrink:0 },

  /* Toolbar */
  toolbar: {
    display:"flex", flexWrap:"wrap", alignItems:"center", gap:3,
    padding:"8px 10px", background:C.surface,
    border:`1px solid ${C.border}`, borderBottom:"none",
  },
  tbtn: {
    width:30, height:30,
    display:"flex", alignItems:"center", justifyContent:"center",
    cursor:"pointer", transition:"all 0.15s", flexShrink:0,
  },
  tbtnLabel: {
    width:30, height:30,
    display:"flex", alignItems:"center", justifyContent:"center",
    cursor:"pointer", color:C.inkMid,
    border:`1px solid ${C.border}`, background:"transparent",
    transition:"all 0.15s", flexShrink:0,
  } as React.CSSProperties,

  /* Editor */
  editor: {
    minHeight:360, padding:"20px 18px",
    fontSize:15, color:C.inkSoft, lineHeight:1.85,
    fontFamily:C.sans, fontWeight:300, outline:"none",
    background:C.paper, transition:"border-color 0.2s",
    boxSizing:"border-box" as const,
  },
  countBar: {
    display:"flex", justifyContent:"space-between",
    fontSize:9, color:C.inkLight, letterSpacing:"0.1em",
    padding:"6px 0 2px",
  },

  /* Link popup */
  linkPopup: {
    position:"absolute" as const, top:"110%", left:0, zIndex:200,
    display:"flex", gap:6, alignItems:"center",
    background:C.paper, border:`1px solid ${C.border}`,
    padding:"8px 10px", boxShadow:"0 4px 20px rgba(0,0,0,0.08)",
    minWidth:300,
  },
  linkInput: {
    flex:1, background:C.surface, border:`1px solid ${C.border}`,
    padding:"8px 10px", fontSize:12, color:C.ink,
    fontFamily:C.sans, outline:"none",
  },
  linkInsertBtn: {
    background:C.ink, color:"#fff", border:"none",
    padding:"8px 14px", fontSize:10, fontWeight:600,
    letterSpacing:"0.14em", cursor:"pointer", flexShrink:0,
  },
  linkCancelBtn: {
    background:"transparent", border:`1px solid ${C.border}`,
    width:30, height:30, display:"flex", alignItems:"center",
    justifyContent:"center", cursor:"pointer", color:C.inkMid, flexShrink:0,
  },

  /* Publish */
  publishBtn: {
    display:"inline-flex", alignItems:"center", justifyContent:"center",
    background:C.ink, color:"#fff", border:"none",
    padding:"14px 36px", fontSize:11, fontWeight:700,
    letterSpacing:"0.22em", fontFamily:C.sans,
    alignSelf:"flex-start" as const, transition:"opacity 0.2s",
  },

  /* ── Sidebar ── */
  sidebar:     { display:"flex", flexDirection:"column", gap:16, position:"sticky" as const, top:20 },
  sideCard:    { border:`1px solid ${C.border}`, padding:"18px 16px", background:C.paper },
  sideCardTitle:{
    fontSize:9, letterSpacing:"0.25em", color:C.ink,
    fontFamily:C.sans, fontWeight:700, marginBottom:14,
  },

  /* Featured image */
  featuredImg: { width:"100%", display:"block", maxHeight:180, objectFit:"cover" },
  featuredRemove: {
    position:"absolute" as const, top:8, right:8,
    background:"rgba(0,0,0,0.6)", border:"none",
    width:24, height:24, borderRadius:"50%",
    display:"flex", alignItems:"center", justifyContent:"center",
    cursor:"pointer", color:"#fff",
  },
  featuredUpload: {
    display:"flex", flexDirection:"column" as const,
    alignItems:"center", justifyContent:"center",
    border:`1px dashed ${C.border}`, padding:"28px 16px",
    cursor:"pointer", textAlign:"center" as const,
    background:C.surface, transition:"border-color 0.2s",
  } as React.CSSProperties,

  /* Category */
  select: {
    width:"100%", appearance:"none" as const, background:C.surface,
    border:`1px solid ${C.border}`, padding:"10px 36px 10px 12px",
    fontSize:12, color:C.ink, fontFamily:C.sans, outline:"none",
    cursor:"pointer",
  },

  /* SEO preview */
  seoPreview: { background:C.surface, padding:"12px 14px" },
  seoTitle:   { fontSize:14, fontWeight:500, color:"#1a0dab", marginBottom:3 },
  seoUrl:     { fontSize:11, color:C.success, marginBottom:4 },
  seoDesc:    { fontSize:11, color:C.inkMid, lineHeight:1.6 },
};

/* ─────────────────────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────────────────────── */
const EPOST_CSS = `
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 0; }

  .ep-input:focus { border-color: #0a0a0a !important; }
  .ep-title:focus { border-color: #0a0a0a !important; }

  .ep-editor:empty::before {
    content: attr(data-placeholder);
    color: #a3a3a3;
    font-size: 15px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 300;
    pointer-events: none;
  }

  .ep-editor h2 { font-family: 'Cormorant Garamond', serif; font-size: clamp(22px, 3vw, 32px); font-weight: 600; color: #0a0a0a; margin: 24px 0 10px; line-height: 1.15; }
  .ep-editor h3 { font-family: 'Cormorant Garamond', serif; font-size: clamp(18px, 2.5vw, 24px); font-weight: 500; color: #2a2a2a; margin: 18px 0 8px; }
  .ep-editor p  { margin-bottom: 12px; line-height: 1.85; }
  .ep-editor a  { color: #0a0a0a; text-decoration: underline; text-underline-offset: 3px; }
  .ep-editor ul, .ep-editor ol { margin: 10px 0 14px 20px; }
  .ep-editor li { margin-bottom: 5px; line-height: 1.7; }
  .ep-editor img { max-width: 100%; border-radius: 4px; margin: 12px 0; display: block; }
  .ep-editor blockquote { border-left: 2px solid #0a0a0a; padding: 4px 0 4px 16px; margin: 16px 0; font-style: italic; color: #2a2a2a; }

  .ep-tbtn:hover { border-color: #0a0a0a !important; color: #0a0a0a !important; }

  .ep-featured-upload:hover { border-color: #0a0a0a !important; }

  @keyframes ep-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  .ep-spin { animation: ep-spin 1s linear infinite; }

  /* ── Desktop: two-column layout ── */
  .ep-layout {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 32px;
    align-items: start;
  }

  /* ── Mobile: single column ── */
  @media (max-width: 720px) {
    .ep-layout {
      grid-template-columns: 1fr !important;
      gap: 24px !important;
    }

    /* Sidebar unsticks so it flows below the editor */
    .ep-layout > div:last-child {
      position: relative !important;
      top: 0 !important;
    }

    /* Body padding */
    .ep-body-wrap { padding: 18px 14px 60px !important; }

    /* Title scale */
    .ep-post-title { font-size: 24px !important; }

    /* Slug prefix — hide on very small screens to save space */
    .ep-slug-prefix { font-size: 10px !important; padding: 0 8px !important; }

    /* Toolbar wraps tightly */
    .ep-toolbar { padding: 6px 8px !important; gap: 2px !important; }

    /* Publish button: full width */
    .ep-publish-btn { width: 100% !important; align-self: stretch !important; }

    /* Editor min height shorter on mobile */
    .ep-editor { min-height: 220px !important; }

    /* Side cards stack cleanly */
    .ep-side-card { padding: 14px 12px !important; }
  }
`;